import React, { useState, useEffect } from 'react';
import {
    Button, Typography, TextField, Box, IconButton,
    InputAdornment,
    MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import toast from 'react-hot-toast';
import LoginDialog from '../../Components/CustomizedModels/LoginModel';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { createUserAction, editUserAction, clearEditUserAction, clearCreateUserAction } from '../../store/actions';

const AddUser = ({ onClose, editMode = false, userData = null, onSuccess }) => {

    const dispatch = useDispatch();

    const [showPassword, setShowPassword] = useState(false);
    const [mobile_no, setmobile_no] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [openLogin, setOpenLogin] = useState(false);

    // Get create and edit user states from Redux
    const { successData: createSuccessData, errorData: createErrorData } = useSelector((state) => state.createUserReducer || {});
    const { successData: editSuccessData, errorData: editErrorData } = useSelector((state) => state.editUserReducer || {});

    // Define the common style for all TextFields
    const textFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
        },
        '& .MuiInputAdornment-root': {
            '& .MuiIconButton-root': {
                color: '#007bff',
            }
        }
    };

    // Initialize form with user data if in edit mode
    const formik = useFormik({
        initialValues: {
            full_name: editMode && userData ? userData.full_name : '',
            email: editMode && userData ? userData.email : '',
            password: '', // Don't pre-fill password in edit mode
            mobile_no: editMode && userData ? userData.mobile_no : '',
            role: editMode && userData ? userData.role : '',
        },
        validationSchema: Yup.object({
            full_name: Yup.string().required('Full Name is required'),
            email: Yup.string().email('Invalid email').required('Email is required'),
            password: editMode
                ? Yup.string().min(6, 'Password must be at least 6 characters') // Optional in edit mode
                : Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
            mobile_no: Yup.string().matches(/^[6-9]\d{9}$/, 'Invalid phone number').required('Phone Number is required'),
            role: Yup.string().required('Role is required'),
        }),
        onSubmit: (values) => {
            setIsLoading(true);
            if (editMode && userData) {
                const updatedValues = { ...values };
                if (!updatedValues.password) {
                    delete updatedValues.password;
                }

                // Use user_id instead of id
                const userId = userData.user_id;

                if (!userId) {
                    toast.error('User ID is missing');
                    setIsLoading(false);
                    return;
                }

                console.log('Updating user data:', updatedValues);
                dispatch(editUserAction({
                    endPoint: `admin/user/edit/${userId}`,
                    ...updatedValues,
                    id: userId,
                }));
            }
            else {
                console.log('Creating new user:', values);
                dispatch(createUserAction(values));
            }
        }
    });

    // Set mobile_no state when initializing form
    useEffect(() => {
        if (editMode && userData) {
            setmobile_no(userData.mobile_no);
        }
    }, [editMode, userData]);

    // Handle success and error responses
    useEffect(() => {
        if (createSuccessData) {
            toast.success('User created successfully');
            setIsLoading(false);
            if (onSuccess) onSuccess();
            onClose();
            dispatch(clearCreateUserAction())
        }
        if (createErrorData) {
            toast.error(createErrorData.message || 'Failed to create user');
            setIsLoading(false);
        }
    }, [createSuccessData, createErrorData]);

    useEffect(() => {
        if (editSuccessData) {
            toast.success('User updated successfully');
            setIsLoading(false);
            dispatch(clearEditUserAction());
            if (onSuccess) onSuccess();
            onClose();
        }
        if (editErrorData) {
            toast.error(editErrorData.message || 'Failed to update user');
            setIsLoading(false);
        }
    }, [editSuccessData, editErrorData]);

    const handleLoginClose = () => {
        setOpenLogin(false);
    };

    const handleOtpLogin = () => {
        setOpenLogin(false);
    };
    const handleLoginSuccess = () => {
        toast.success('Logged in successfully');
        handleLoginClose();
    };

    const handleLoginError = (errorMessage) => {
        toast.error(errorMessage || 'Login failed');
        console.error('Login failed:', errorMessage);
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff', padding: '50px' }}>
            <IconButton
                onClick={onClose}
                sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ width: '100%', backgroundColor: '#fff', display: 'flex', borderRadius: '8px' }}>
                <Box sx={{ flex: 1, position: 'relative' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: '2%', mb: '2%' }}>
                        {editMode ? 'Edit User' : 'Create New User'}
                    </Typography>
                    <form onSubmit={formik.handleSubmit} >
                        <Grid container spacing={2}>
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    name="full_name"
                                    variant="outlined"
                                    size="small"
                                    value={formik.values.full_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                                    helperText={formik.touched.full_name && formik.errors.full_name}
                                    sx={textFieldStyle}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    variant="outlined"
                                    size="small"
                                    type="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    sx={textFieldStyle}
                                />
                            </Grid>

                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="mobile_no"
                                    variant="outlined"
                                    size="small"
                                    value={formik.values.mobile_no}
                                    onChange={(e) => {
                                        formik.handleChange(e);
                                        setmobile_no(e.target.value);
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.mobile_no && Boolean(formik.errors.mobile_no)}
                                    helperText={formik.touched.mobile_no && formik.errors.mobile_no}
                                    inputProps={{ maxLength: 10 }}
                                    sx={textFieldStyle}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Role"
                                    name="role"
                                    variant="outlined"
                                    size="small"
                                    value={formik.values.role}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.role && Boolean(formik.errors.role)}
                                    helperText={formik.touched.role && formik.errors.role}
                                    disabled={editMode}
                                    sx={textFieldStyle}
                                >
                                    <MenuItem value="" disabled>Select Role</MenuItem>
                                    <MenuItem value="admin" disabled>Admin</MenuItem>
                                    <MenuItem value="stock_manager">Stock Manager</MenuItem>
                                    <MenuItem value="production_manager">Production Manager</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label={editMode ? "New Password" : "Password"}
                                    name="password"
                                    variant="outlined"
                                    size="small"
                                    type={showPassword ? "text" : "password"}
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={textFieldStyle}
                                />
                            </Grid>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{ backgroundColor: '#007bff', color: '#fff', borderRadius: '15px', width: { xs: '250px', sm: '350px' } }}
                            >
                                {isLoading ? 'Processing...' : (editMode ? 'Update User' : 'Submit')}
                            </Button>
                        </Grid>
                    </form>
                </Box>
            </Box>
            <LoginDialog
                open={openLogin}
                onClose={handleLoginClose}
                onOtpLogin={handleOtpLogin}
                onLoginSuccess={handleLoginSuccess}
                onLoginError={handleLoginError}
            />
        </Box>
    );
};

export default AddUser;
